import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Webhook } from "lucide-react";

interface Postback {
  id: number;
  offerId: number;
  postbackUrl: string;
  eventName: string | null;
  status: string;
  createdAt: string;
}

export function AffPostbacksPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["aff-portal", "postbacks"],
    queryFn: async () => {
      const res = await api.get("/v1/portal/affiliate/postbacks");
      if (!res.ok) throw new Error("Failed to load postbacks");
      return (await res.json()).data as Postback[];
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Postbacks</h1>
        <p className="text-sm text-gray-500 mt-1">Your configured conversion postback URLs</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-20" />)}
        </div>
      ) : !data?.length ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Webhook className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No postbacks configured.</p>
          <p className="text-xs text-gray-400 mt-1">Contact your account manager to set up postback URLs.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Offer ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Postback URL</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Event</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((pb) => (
                <tr key={pb.id} className="border-t border-gray-50">
                  <td className="py-3 px-4 text-gray-700">{pb.offerId || "Global"}</td>
                  <td className="py-3 px-4 text-gray-600 text-xs font-mono truncate max-w-[400px]">{pb.postbackUrl}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{pb.eventName || "All"}</td>
                  <td className="py-3 px-4">
                    <span className={cn(
                      "text-[10px] font-medium px-1.5 py-0.5 rounded uppercase",
                      pb.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500",
                    )}>
                      {pb.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
